/**
 * TaskQuickView — popup for one `tasks` doc, opened from a notification
 * (?task=<id>) or openTask(). Shows the task, the submitted update, and the
 * review trail; the named reviewer approves (closes) or returns it here.
 */
import { useEffect, useState } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import {
  REVIEW_STATUS, STATUS_LABEL, assigneesOf,
  approveTask, returnTask, withdrawReview, requestTaskCompletion,
} from '../../utils/taskClosure';

const S = {
  bg: 'var(--smc-bg)', panel: 'var(--smc-panel)', border: 'var(--smc-bd)',
  text: 'var(--smc-tx)', dim: 'var(--smc-tx3)', muted: 'var(--smc-tx2)', accent: 'var(--smc-ac)',
  green: 'var(--smc-gn)', red: 'var(--smc-rd)', amber: 'var(--smc-am)',
  mono: 'var(--smc-mono, monospace)', sans: "'Inter', system-ui, sans-serif",
};
const STATUS_COLOR = {
  pending: S.amber, in_progress: S.accent, [REVIEW_STATUS]: 'var(--smc-pu)', completed: S.green, blocked: S.red,
};
const HISTORY_LABEL = {
  submitted: 'sent it for approval', approved: 'approved and closed it',
  returned: 'returned it', withdrawn: 'withdrew it from review',
};

const toDate = v => (!v ? null : v.toDate ? v.toDate() : new Date(v));
const fmt = v => {
  const d = toDate(v);
  return d && !isNaN(d) ? d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
};

function Section({ title, children, color }) {
  return (
    <div style={{ padding: '12px 18px', borderBottom: `1px solid ${S.border}` }}>
      <div style={{ fontFamily: S.mono, fontSize: 9, color: color || S.dim, letterSpacing: 1.5, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Files({ items }) {
  if (!items?.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
      {items.map((a, i) => (
        <a key={a.path || a.url || i} href={a.url} target="_blank" rel="noopener noreferrer"
          style={{ fontFamily: S.mono, fontSize: 10, color: S.accent, padding: '4px 8px',
            border: `1px solid ${S.border}`, borderRadius: 4, textDecoration: 'none' }}>
          📎 {a.name || 'File'}
        </a>
      ))}
    </div>
  );
}

export default function TaskQuickView({ taskId, user, onClose }) {
  const [task, setTask] = useState(undefined);   // undefined = loading, null = missing
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => onSnapshot(doc(getFirestore(getApp()), 'tasks', taskId),
    snap => setTask(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    () => setTask(null)), [taskId]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function act(kind) {
    setBusy(kind);
    setError('');
    try {
      if (kind === 'approve') await approveTask(task, { note, user });
      if (kind === 'return') await returnTask(task, { note, user });
      if (kind === 'withdraw') await withdrawReview(task, { user });
      setNote('');
    } catch (ex) {
      setError(ex.code === 'permission-denied' ? 'Not allowed — only the named reviewer can do this.' : (ex.message || 'Could not save'));
    }
    setBusy('');
  }

  const review = task?.review || {};
  const assignees = task ? assigneesOf(task) : [];
  const names = task?.assigneeNames || (task?.assigneeName ? [task.assigneeName] : []);
  const inReview = task?.status === REVIEW_STATUS;
  const isReviewer = inReview && review.reviewerUid === user.uid && review.submittedByUid !== user.uid;
  const isSubmitter = inReview && review.submittedByUid === user.uid;
  const canSubmit = task && !inReview && task.status !== 'completed' &&
    (assignees.includes(user.uid) || task.createdByUid === user.uid);
  const history = (task?.reviewHistory || []).slice().sort((a, b) => (a.at || '').localeCompare(b.at || ''));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 880, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={task?.title || 'Task'}
        style={{ width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', background: S.panel,
          border: `1px solid ${S.border}`, borderRadius: 12, color: S.text, fontFamily: S.sans,
          borderLeft: `4px solid ${STATUS_COLOR[task?.status] || S.border}` }}>
        <div style={{ padding: '16px 18px', borderBottom: `1px solid ${S.border}`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: 2, marginBottom: 4 }}>TASK</div>
            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35 }}>
              {task === undefined ? 'Loading…' : task === null ? 'Task not found' : task.title}
            </div>
            {task && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontFamily: S.mono, fontSize: 9, color: STATUS_COLOR[task.status] || S.muted,
                  border: '1px solid currentColor', padding: '1px 8px', borderRadius: 10 }}>
                  {(STATUS_LABEL[task.status] || task.status || 'Pending').toUpperCase()}
                </span>
                {task.priority && <span style={{ fontFamily: S.mono, fontSize: 9, color: S.muted }}>{task.priority.toUpperCase()}</span>}
                {task.dueDate && <span style={{ fontFamily: S.mono, fontSize: 9, color: S.muted }}>DUE {task.dueDate}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', color: S.dim, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        {task === null && (
          <div style={{ padding: 24, color: S.muted, fontSize: 13 }}>It may have been removed, or you don't have access to it.</div>
        )}

        {task && (
          <>
            {task.description && (
              <Section title="DESCRIPTION">
                <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{task.description}</div>
              </Section>
            )}
            <Section title="PEOPLE">
              <div style={{ fontSize: 12, color: S.muted, lineHeight: 1.8 }}>
                <div>Created by <b style={{ color: S.text }}>{task.createdBy || '—'}</b></div>
                <div>Assigned to <b style={{ color: S.text }}>{names.length ? names.join(', ') : (assignees.length ? `${assignees.length} member(s)` : 'department / everyone')}</b></div>
                {review.reviewerName && <div>Reviewer <b style={{ color: S.text }}>{review.reviewerName}</b></div>}
              </div>
            </Section>

            {(inReview || task.status === 'completed') && (task.completionNote || task.completionAttachments?.length > 0) && (
              <Section title={`UPDATE FROM ${(review.submittedByName || task.completedByName || 'ASSIGNEE').toUpperCase()}`} color="var(--smc-pu)">
                {task.completionNote && <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{task.completionNote}</div>}
                <Files items={task.completionAttachments} />
              </Section>
            )}

            {task.status === 'completed' && task.approvedByName && (
              <Section title="CLOSED" color={S.green}>
                <div style={{ fontSize: 13 }}>
                  Approved by <b>{task.approvedByName}</b>{task.approvedAt ? ` · ${fmt(task.approvedAt)}` : ''}
                </div>
                {task.approvalNote && <div style={{ fontSize: 13, color: S.muted, marginTop: 4, whiteSpace: 'pre-wrap' }}>{task.approvalNote}</div>}
              </Section>
            )}

            {task.status !== REVIEW_STATUS && task.status !== 'completed' && task.reviewReturnNote && (
              <Section title={`RETURNED BY ${(task.reviewReturnedByName || 'REVIEWER').toUpperCase()}`} color={S.amber}>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{task.reviewReturnNote}</div>
              </Section>
            )}

            {isReviewer && (
              <Section title="YOUR REVIEW" color={S.accent}>
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} aria-label="Review note"
                  placeholder="Note for the assignee (required to return, optional to approve)"
                  style={{ width: '100%', boxSizing: 'border-box', background: S.bg, border: `1px solid ${S.border}`,
                    color: S.text, fontFamily: S.sans, fontSize: 13, padding: '8px 10px', borderRadius: 6, resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => act('approve')} disabled={!!busy}
                    style={{ flex: '1 1 160px', padding: '9px 0', fontFamily: S.mono, fontSize: 10, letterSpacing: 1,
                      background: S.green, border: `1px solid ${S.green}`, color: '#fff', borderRadius: 6, cursor: 'pointer' }}>
                    {busy === 'approve' ? 'CLOSING…' : '✓ APPROVE & CLOSE'}
                  </button>
                  <button onClick={() => act('return')} disabled={!!busy || !note.trim()}
                    title={note.trim() ? '' : 'Write what is missing first'}
                    style={{ flex: '1 1 160px', padding: '9px 0', fontFamily: S.mono, fontSize: 10, letterSpacing: 1,
                      background: 'transparent', border: `1px solid ${note.trim() ? S.amber : S.border}`,
                      color: note.trim() ? S.amber : S.dim, borderRadius: 6, cursor: note.trim() ? 'pointer' : 'default' }}>
                    {busy === 'return' ? 'RETURNING…' : '↩ RETURN WITH NOTE'}
                  </button>
                </div>
              </Section>
            )}

            {isSubmitter && (
              <Section title="WAITING FOR APPROVAL">
                <div style={{ fontSize: 13, color: S.muted }}>
                  Sent to <b style={{ color: S.text }}>{review.reviewerName || 'the reviewer'}</b>{review.submittedAt ? ` · ${fmt(review.submittedAt)}` : ''}.
                </div>
                <button onClick={() => act('withdraw')} disabled={!!busy}
                  style={{ marginTop: 8, padding: '6px 12px', fontFamily: S.mono, fontSize: 9, background: 'transparent',
                    border: `1px solid ${S.border}`, color: S.muted, borderRadius: 4, cursor: 'pointer' }}>
                  {busy === 'withdraw' ? '…' : 'WITHDRAW TO EDIT'}
                </button>
              </Section>
            )}

            {inReview && !isReviewer && !isSubmitter && (
              <Section title="IN REVIEW">
                <div style={{ fontSize: 13, color: S.muted }}>Waiting for {review.reviewerName || 'the reviewer'} to approve.</div>
              </Section>
            )}

            {canSubmit && (
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${S.border}` }}>
                <button onClick={() => requestTaskCompletion(task.id)}
                  style={{ width: '100%', padding: '10px 0', fontFamily: S.mono, fontSize: 10, letterSpacing: 1,
                    background: 'transparent', border: `1px solid ${S.green}`, color: S.green, borderRadius: 6, cursor: 'pointer' }}>
                  ✓ MARK COMPLETE — SEND FOR APPROVAL
                </button>
              </div>
            )}

            {history.length > 0 && (
              <Section title="HISTORY">
                {history.map((h, i) => (
                  <div key={`${h.at}-${i}`} style={{ fontSize: 12, color: S.muted, marginBottom: 4 }}>
                    <b style={{ color: S.text }}>{h.byName || 'Someone'}</b> {HISTORY_LABEL[h.action] || h.action}
                    <span style={{ color: S.dim }}> · {fmt(h.at)}</span>
                    {h.note && h.action !== 'submitted' && <div style={{ marginLeft: 10, whiteSpace: 'pre-wrap' }}>“{h.note}”</div>}
                  </div>
                ))}
              </Section>
            )}
            {error && <div role="alert" style={{ padding: '10px 18px', fontFamily: S.mono, fontSize: 10, color: S.red }}>{error}</div>}
          </>
        )}
      </div>
    </div>
  );
}
